'use client';

import { useState, useEffect } from 'react';
import { settingsStorage, sectionVisibilityStorage } from '@/lib/storage-hybrid';

export interface Settings {
  id: string;
  companyName: string;
  tagline: string;
  description: string;
  email?: string;
  phone?: string;
  address?: string;
  linkedin?: string;
  github?: string;
  twitter?: string;
  instagram?: string;
  facebook?: string;
  primaryColor?: string;
  secondaryColor?: string;
  favicon?: string;
  logo?: string;
}

export interface SectionVisibility {
  heroVisible: boolean;
  servicesVisible: boolean;
  portfolioVisible: boolean;
  aboutVisible: boolean;
  teamVisible: boolean;
  testimonialsVisible: boolean;
  statsVisible: boolean;
  pricingVisible: boolean;
  blogVisible: boolean;
  contactVisible: boolean;
  footerVisible: boolean;
  [key: string]: boolean;
}

const DEFAULT_VISIBILITY: SectionVisibility = {
  heroVisible: true,
  servicesVisible: true,
  portfolioVisible: true,
  aboutVisible: true,
  teamVisible: true,
  testimonialsVisible: true,
  statsVisible: true,
  pricingVisible: true,
  blogVisible: true,
  contactVisible: true,
  footerVisible: true,
};

/**
 * Hook to read and listen for changes in site settings
 * Automatically updates when settings are saved in admin panel
 */
export function useSettings() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initial load
    const loadSettings = async () => {
      try {
        const data = await settingsStorage.get();
        setSettings(data);
      } catch (error) {
        console.error('Failed to load settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();

    // Listen for storage changes
    const handleStorageChange = async () => {
      const data = await settingsStorage.get();
      setSettings(data);
    };

    // Create a custom event listener for storage changes
    const interval = setInterval(handleStorageChange, 500);

    // Also listen to storage events from other tabs
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return { settings, isLoading };
}

/**
 * Hook to read and listen for changes in section visibility
 * Automatically updates when visibility settings are saved in admin panel
 */
export function useSectionVisibility() {
  const [visibility, setVisibility] = useState<SectionVisibility>(DEFAULT_VISIBILITY);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initial load
    const loadVisibility = async () => {
      try {
        const data = await sectionVisibilityStorage.get();
        setVisibility(data || DEFAULT_VISIBILITY);
      } catch (error) {
        console.error('Failed to load visibility:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadVisibility();

    // Listen for storage changes
    const handleStorageChange = async () => {
      const data = await sectionVisibilityStorage.get();
      setVisibility(data || DEFAULT_VISIBILITY);
    };

    // Create a custom event listener for storage changes
    const interval = setInterval(handleStorageChange, 500);

    // Also listen to storage events from other tabs
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return { visibility, isLoading };
}

/**
 * Hook to read both settings and visibility
 * Use this when you need both at the same time
 */
export function useSiteConfig() {
  const { settings, isLoading: settingsLoading } = useSettings();
  const { visibility, isLoading: visibilityLoading } = useSectionVisibility();

  return {
    settings,
    visibility,
    isLoading: settingsLoading || visibilityLoading,
  };
}
